import apiClient from './client'
import type { PaginatedResponse } from '@/types'

// Types
export interface TaskForce {
  id: number
  name: string
  slug: string
  description: string
  objectives: string
  leader: {
    id: number
    full_name: string
    avatar: string | null
  }
  members_count: number
  tickets_count: number
  is_active: boolean
  is_member?: boolean
  user_role?: 'leader' | 'coordinator' | 'member' | null
  created_at: string
  end_date: string | null
}

export interface TaskForceMember {
  id: number
  user: {
    id: number
    full_name: string
    email: string
    avatar: string | null
    role: string
  }
  taskforce_role: 'leader' | 'coordinator' | 'member'
  joined_at: string
  is_active: boolean
}

export interface Ticket {
  id: number
  taskforce_id: number
  title: string
  description: string
  status: 'open' | 'in_progress' | 'review' | 'completed' | 'closed'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  assigned_to: {
    id: number
    full_name: string
    avatar: string | null
  } | null
  created_by: {
    id: number
    full_name: string
  }
  due_date: string | null
  tags: string[]
  comments_count: number
  attachments_count: number
  created_at: string
  updated_at: string
}

export interface TicketComment {
  id: number
  ticket_id: number
  author: {
    id: number
    full_name: string
    avatar: string | null
  }
  content: string
  created_at: string
}

export interface TaskForceDocument {
  id: number
  taskforce_id: number
  title: string
  description: string
  file_url: string
  file_type: string
  uploaded_by: {
    id: number
    full_name: string
  }
  created_at: string
}

// Chat Types
export interface ChatMessage {
  id: number
  taskforce: number
  sender: number
  sender_name: string
  sender_avatar: string | null
  content: string
  message_type: 'text' | 'file' | 'image' | 'system'
  attachment: number | null
  reply_to: number | null
  reply_to_preview: {
    id: number
    content: string
    sender_name: string
  } | null
  is_edited: boolean
  edited_at: string | null
  is_deleted: boolean
  created_at: string
}

export interface ChatMessagesResponse {
  messages: ChatMessage[]
  has_more: boolean
}

export interface OnlineMember {
  id: number
  name: string
}

export const taskforceApi = {
  // Task Forces
  getTaskForces: async (params?: {
    search?: string
    status?: 'active' | 'completed'
    page?: number
  }): Promise<PaginatedResponse<TaskForce>> => {
    const response = await apiClient.get('/taskforce/taskforces/', { params })
    return response.data
  },

  getTaskForce: async (slug: string): Promise<TaskForce & {
    members: TaskForceMember[]
    recent_tickets: Ticket[]
  }> => {
    const response = await apiClient.get(`/taskforce/taskforces/${slug}/`)
    return response.data
  },

  getMyTaskForces: async (): Promise<TaskForce[]> => {
    const response = await apiClient.get('/taskforce/taskforces/my/')
    // Handle both array and paginated response
    if (Array.isArray(response.data)) {
      return response.data
    }
    if (response.data?.results && Array.isArray(response.data.results)) {
      return response.data.results
    }
    return []
  },

  // Membership
  joinTaskForce: async (slug: string): Promise<void> => {
    await apiClient.post(`/taskforce/taskforces/${slug}/join/`)
  },

  leaveTaskForce: async (slug: string): Promise<void> => {
    await apiClient.post(`/taskforce/taskforces/${slug}/leave/`)
  },

  getMembers: async (taskforceId: number): Promise<TaskForceMember[]> => {
    const response = await apiClient.get(`/taskforce/taskforces/${taskforceId}/members/`)
    return response.data
  },

  // Tickets
  getTickets: async (
    taskforceId: number,
    params?: {
      status?: string
      priority?: string
      assigned_to?: number
      search?: string
      page?: number
    }
  ): Promise<PaginatedResponse<Ticket>> => {
    const response = await apiClient.get(`/taskforce/taskforces/${taskforceId}/tickets/`, { params })
    return response.data
  },

  getTicket: async (taskforceId: number, ticketId: number): Promise<Ticket & {
    comments: TicketComment[]
    attachments: { id: number; name: string; url: string }[]
    activity: {
      action: string
      user: string
      timestamp: string
      details?: string
    }[]
  }> => {
    const response = await apiClient.get(`/taskforce/taskforces/${taskforceId}/tickets/${ticketId}/`)
    return response.data
  },

  createTicket: async (taskforceId: number, data: {
    title: string
    description: string
    priority?: Ticket['priority']
    status?: 'open' | 'in_progress' | 'review' | 'completed'
    assigned_to_id?: number
    due_date?: string
    tags?: string[]
  }): Promise<Ticket> => {
    const response = await apiClient.post(`/taskforce/tickets/`, {
      ...data,
      taskforce: taskforceId,
      assignee: data.assigned_to_id || null,
    })
    return response.data
  },

  updateTicket: async (
    taskforceId: number,
    ticketId: number,
    data: Partial<{
      title: string
      description: string
      status: Ticket['status']
      priority: Ticket['priority']
      assigned_to_id: number | null
      due_date: string | null
      tags: string[]
    }>
  ): Promise<Ticket> => {
    const response = await apiClient.patch(
      `/taskforce/taskforces/${taskforceId}/tickets/${ticketId}/`,
      data
    )
    return response.data
  },

  deleteTicket: async (taskforceId: number, ticketId: number): Promise<void> => {
    await apiClient.delete(`/taskforce/taskforces/${taskforceId}/tickets/${ticketId}/`)
  },

  // Ticket Comments
  addComment: async (
    taskforceId: number,
    ticketId: number,
    content: string
  ): Promise<TicketComment> => {
    const response = await apiClient.post(
      `/taskforce/taskforces/${taskforceId}/tickets/${ticketId}/comments/`,
      { content }
    )
    return response.data
  },

  // Documents
  getDocuments: async (taskforceId: number): Promise<TaskForceDocument[]> => {
    const response = await apiClient.get(`/taskforce/taskforces/${taskforceId}/documents/`)
    return response.data
  },

  uploadDocument: async (taskforceId: number, data: FormData): Promise<TaskForceDocument> => {
    const response = await apiClient.post(
      `/taskforce/taskforces/${taskforceId}/documents/`,
      data,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },

  deleteDocument: async (taskforceId: number, documentId: number): Promise<void> => {
    await apiClient.delete(`/taskforce/taskforces/${taskforceId}/documents/${documentId}/`)
  },

  // Stats
  getTaskForceStats: async (taskforceId: number): Promise<{
    tickets_total: number
    tickets_open: number
    tickets_in_progress: number
    tickets_completed: number
    members_active: number
    avg_completion_time_days: number
  }> => {
    const response = await apiClient.get(`/taskforce/taskforces/${taskforceId}/stats/`)
    return response.data
  },

  // Kanban Board
  getKanbanData: async (taskforceSlug: string): Promise<{
    taskforce: { id: number; name: string; slug: string }
    columns: {
      open: { title: string; tickets: Ticket[] }
      in_progress: { title: string; tickets: Ticket[] }
      review: { title: string; tickets: Ticket[] }
      done: { title: string; tickets: Ticket[] }
    }
  }> => {
    const response = await apiClient.get('/taskforce/tickets/kanban/', {
      params: { taskforce_slug: taskforceSlug }
    })
    return response.data
  },

  reorderTicket: async (data: {
    ticket_id: number
    new_status: 'open' | 'in_progress' | 'review' | 'done'
    position: number
    taskforce_slug: string
  }): Promise<{ detail: string; ticket: Ticket }> => {
    const response = await apiClient.post('/taskforce/tickets/reorder/', data)
    return response.data
  },

  // PDF Export
  exportPdf: async (taskforceSlug: string): Promise<Blob> => {
    const response = await apiClient.get(`/taskforce/taskforces/${taskforceSlug}/export_pdf/`, {
      responseType: 'blob'
    })
    return response.data
  },

  // Chat Messages
  getChatMessages: async (taskforceSlug: string, params?: {
    limit?: number
    before?: number
  }): Promise<ChatMessagesResponse> => {
    const response = await apiClient.get(`/taskforce/taskforces/${taskforceSlug}/messages/`, { params })
    return response.data
  },

  sendChatMessage: async (taskforceSlug: string, data: {
    content: string
    reply_to?: number
  }): Promise<ChatMessage> => {
    const response = await apiClient.post(`/taskforce/taskforces/${taskforceSlug}/messages/`, data)
    return response.data
  },

  getOnlineMembers: async (taskforceSlug: string): Promise<{ online_members: OnlineMember[] }> => {
    const response = await apiClient.get(`/taskforce/taskforces/${taskforceSlug}/online_members/`)
    return response.data
  },
}
